import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpDown, Check, ChevronDown } from "lucide-react";
import { api, discountOf } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import CategoryNav, { UniverseCards, visibleOrdered } from "@/components/CategoryNav";
import { Reveal } from "@/components/Reveal";

const SORTS = [
  { v: "new", label: "Nouveautés" },
  { v: "sales", label: "Meilleures ventes" },
  { v: "price-asc", label: "Prix croissant" },
  { v: "price-desc", label: "Prix décroissant" },
  { v: "discount", label: "Réduction la plus importante" },
];

function SortSelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = SORTS.find((s) => s.v === value) || SORTS[0];

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const esc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        data-testid="filter-sort"
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2.5 rounded-2xl border px-5 py-2.5 text-xs font-bold transition-all duration-300 ${
          open ? "border-kay-pink bg-white shadow-[0_10px_30px_rgba(232,160,191,0.25)]" : "border-kay-line bg-white hover:border-kay-pink"
        }`}
      >
        <ArrowUpDown size={13} className="text-kay-pink" />
        <span className="font-display italic text-sm normal-case">{current.label}</span>
        <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.25 }}>
          <ChevronDown size={14} className="text-kay-pink" />
        </motion.span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.97 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className="absolute left-0 top-full mt-2 z-40 min-w-[230px] rounded-2xl border border-kay-line bg-white/95 backdrop-blur-xl shadow-[0_25px_60px_rgba(224,82,151,0.18)] p-2 grid gap-1"
            data-testid="sort-menu"
          >
            {SORTS.map((s) => (
              <button
                key={s.v}
                type="button"
                data-testid={`sort-option-${s.v}`}
                onClick={() => { onChange(s.v); setOpen(false); }}
                className={`flex items-center justify-between rounded-xl px-4 py-2.5 text-left text-[13px] transition-all ${
                  value === s.v ? "bg-kay-rose/60 font-bold text-kay-wine" : "hover:bg-kay-rose/30 text-kay-ink/70"
                }`}
              >
                <span className={value === s.v ? "font-display italic" : ""}>{s.label}</span>
                {value === s.v && <Check size={14} className="text-kay-pink" />}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Boutique() {
  const { category } = useParams();
  const [products, setProducts] = useState(null);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(false);
  const [sub, setSub] = useState(null);
  const [brand, setBrand] = useState("all");
  const [sort, setSort] = useState("new");

  const load = () => {
    setError(false);
    api
      .get("/products", { params: category ? { category } : {} })
      .then((r) => setProducts(r.data.filter((p) => !p.coming_soon)))
      .catch(() => setError(true));
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  };

  useEffect(() => {
    setSub(null);
    setBrand("all");
    load();
    window.scrollTo({ top: 0, behavior: "smooth" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const cats = visibleOrdered(settings?.categories);
  const currentCat = category ? cats.find((c) => c.slug === category) : null;
  const invalidCat = !!category && settings && !currentCat;

  // Produits dans le périmètre actuel (sous-catégorie), avant filtre marque
  const scoped = useMemo(() => {
    let list = products || [];
    if (sub) list = list.filter((p) => p.subcategory === sub);
    return list;
  }, [products, sub]);

  const brandList = useMemo(() => [...new Set(scoped.map((p) => p.brand).filter(Boolean))].sort(), [scoped]);

  useEffect(() => {
    if (brand !== "all" && !brandList.includes(brand)) setBrand("all");
  }, [brandList, brand]);

  const filtered = useMemo(() => {
    let list = [...scoped];
    if (brand !== "all") list = list.filter((p) => p.brand === brand);
    switch (sort) {
      case "sales":
        list.sort((a, b) => (b.sales ?? 0) - (a.sales ?? 0));
        break;
      case "price-asc":
        list.sort((a, b) => (a.price ?? 0) - (b.price ?? 0));
        break;
      case "price-desc":
        list.sort((a, b) => (b.price ?? 0) - (a.price ?? 0));
        break;
      case "discount":
        list.sort((a, b) => discountOf(b) - discountOf(a));
        break;
      default:
        list.sort((a, b) => Number(b.is_new) - Number(a.is_new));
    }
    return list;
  }, [scoped, brand, sort]);

  if (invalidCat) return <Navigate to="/boutique" replace />;

  const title = currentCat ? currentCat.label : "Boutique";
  const subtitle = currentCat
    ? `La sélection ${currentCat.label.toLowerCase()}, grandes marques à petits prix.`
    : "Toute la sélection, toutes les marques, tous les petits prix.";

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-14 pb-10" data-testid="boutique-page">
      <Reveal>
        <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-kay-pink mb-3">Kay Beauty</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl" data-testid="boutique-title">
          {title} <span className="italic text-kay-pink">.</span>
        </h1>
        <p className="mt-4 text-kay-ink/60 text-base max-w-lg">{subtitle}</p>
      </Reveal>

      {/* Grandes cartes univers : chaque carte ouvre sa page dédiée */}
      <div className="mt-10">
        <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-kay-pink mb-4">Explorer par univers</p>
        <UniverseCards products={products || []} settings={settings} activeSlug={category || null} />
      </div>

      <CategoryNav
        products={products || []}
        settings={settings}
        category={category || null}
        sub={sub}
        brand={brand}
        onSub={setSub}
        onBrand={setBrand}
      />

      <div className="mt-8 flex flex-wrap items-center gap-3 border-y border-kay-line py-4" data-testid="filter-bar">
        <SortSelect value={sort} onChange={setSort} />
        <span className="font-mono text-[11px] uppercase tracking-widest text-kay-ink/40 ml-auto" data-testid="product-count">
          {filtered.length} produit{filtered.length > 1 ? "s" : ""}
        </span>
      </div>

      {error ? (
        <div className="py-24 text-center" data-testid="boutique-error">
          <p className="font-display italic text-3xl text-kay-ink/50">Oups, la boutique n'a pas chargé…</p>
          <button onClick={load} data-testid="boutique-retry" className="mt-6 rounded-full bg-kay-ink text-white px-7 py-3 text-sm font-semibold hover:bg-kay-pink transition-colors">
            Réessayer
          </button>
        </div>
      ) : products === null ? (
        <div className="py-24 text-center font-display italic text-2xl text-kay-ink/40" data-testid="boutique-loading">Chargement des pépites…</div>
      ) : filtered.length === 0 ? (
        <div className="py-24 text-center">
          <p className="font-display italic text-3xl text-kay-ink/50">Aucune pépite ici pour le moment…</p>
          <Link to="/idees" className="mt-6 inline-block rounded-full bg-kay-ink text-white px-7 py-3 text-sm font-semibold hover:bg-kay-pink transition-colors">
            Proposer un produit
          </Link>
        </div>
      ) : (
        <AnimatePresence mode="wait">
          <motion.div
            key={`${category || "all"}-${sub || "all"}-${brand}-${sort}`}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-8"
          >
            {filtered.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} onChanged={load} />
            ))}
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
}
